/* eslint-disable react/prop-types */
import { useEffect, useMemo, useState } from "react";
import { FaXmark } from "react-icons/fa6";
import toast from "react-hot-toast";
import useAuth from "@/hooks/useAuth";
import { getAllPrinterApi } from "@/api/settings/printer-settings";
import { printKitchenCopy } from "@/lib/print-slip";

function PrintKitchenCopyModal({ isOpen, onClose, order, isTabletView = false }) {
  const { user } = useAuth();
  const [printers, setPrinters] = useState([]);
  const [loadingPrinters, setLoadingPrinters] = useState(false);
  const [selectedPrinterId, setSelectedPrinterId] = useState("");
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [isPrinting, setIsPrinting] = useState(false);

  const items = useMemo(() => order?.items || [], [order?.items]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const initialItemIds = (items || [])
      .map((item) => item?.order_item_id ?? item?.id)
      .filter((id) => id !== undefined && id !== null);

    setSelectedItemIds(initialItemIds);
  }, [isOpen, items]);

  useEffect(() => {
    if (!isOpen || !user?.restaurant_id) {
      return;
    }

    setLoadingPrinters(true);
    getAllPrinterApi(user.restaurant_id)
      .then((res) => {
        if (res?.status === "success") {
          const activePrinters = (res?.data?.printers || []).filter(
            (printer) => printer?.status === "active" && printer?.printer_type === "kitchen"
          );

          setPrinters(activePrinters);

          if (activePrinters.length > 0) {
            setSelectedPrinterId(`${activePrinters[0]?.id}`);
          } else {
            setSelectedPrinterId("");
          }
        } else {
          toast.error(res?.message || "Failed to load printers");
          setPrinters([]);
          setSelectedPrinterId("");
        }
      })
      .finally(() => setLoadingPrinters(false));
  }, [isOpen, user?.restaurant_id]);

  const toggleItem = (itemId) => {
    const normalizedId = `${itemId}`;

    setSelectedItemIds((prev) => {
      if (prev.some((id) => `${id}` === normalizedId)) {
        return prev.filter((id) => `${id}` !== normalizedId);
      }

      return [...prev, itemId];
    });
  };

  const getPrice = (item) => {
    if (item?.is_complementary || item?.is_loyalty_free || item?.is_offer_free || item?.is_free || item?.calculated_price === 0) {
      return "Free";
    }

    return (
      item?.price ||
      item?.variant?.price ||
      item?.in_price ||
      item?.out_price ||
      item?.variant?.in_price ||
      item?.variant?.out_price ||
      item?.adult_price ||
      item?.kids_price ||
      0
    );
  };

  const handlePrint = async () => {
    const targetOrderId = order?.order_id || order?.id;

    if (!targetOrderId) {
      toast.error("Order not found");
      return;
    }

    if (!selectedPrinterId) {
      toast.error("Please select a printer");
      return;
    }

    if (selectedItemIds.length === 0) {
      toast.error("Please select at least one item");
      return;
    }

    setIsPrinting(true);
    const res = await printKitchenCopy(targetOrderId, {
      printerId: selectedPrinterId,
      itemIds: selectedItemIds,
    });
    setIsPrinting(false);

    if (res?.status === "success") {
      onClose();
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center px-2">
      <div className={`${isTabletView ? "w-[95%] max-w-[680px]" : "w-[700px]"} bg-color2 rounded-xl border-2 border-color3 p-4 sm:p-6 relative`}>
        <button
          onClick={onClose}
          className="absolute right-3 top-3 text-white/80 hover:text-white"
        >
          <FaXmark size={22} />
        </button>

        <h2 className="text-center text-2xl text-color5 font-semibold mb-4">Print Kitchen Copy</h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3 bg-color3 p-3 rounded-md">
          <p className="text-sm text-color6">Order ID: {order?.order_summary?.online_order_unique_id || order?.order_id || order?.id || "-"}</p>
          <p className="text-sm text-right text-color6">Date: {order?.order_summary?.order_time_date || (order?.created_at ? new Date(order.created_at).toLocaleDateString() : "-")}</p>
          <p className="text-sm text-color6">Customer Name: {order?.customer_info?.customer_name || order?.customer?.full_name || order?.customer_full_name || "Walk-in"}</p>
          <p className="text-sm text-right text-color6">Tel: {order?.customer_info?.phone_number || order?.customer?.number || order?.customer_number || "-"}</p>
        </div>

        <div className="mb-3">
          <label className="text-sm text-color6 block mb-1">Kitchen Printer</label>
          <select
            value={selectedPrinterId}
            onChange={(e) => setSelectedPrinterId(e.target.value)}
            disabled={loadingPrinters || isPrinting}
            className="w-full bg-color3 border border-color4 rounded-md text-color5 px-3 py-2 outline-none"
          >
            <option value="">Select printer</option>
            {printers.map((printer) => (
              <option key={printer?.id} value={printer?.id}>
                {printer?.name}
              </option>
            ))}
          </select>
        </div>

        <h3 className="text-xl text-color5 mb-2">Items</h3>
        <div className="max-h-[280px] overflow-y-auto space-y-1 pr-1">
          {items.map((item, index) => {
            const itemId = item?.order_item_id ?? item?.id;
            const isChecked = selectedItemIds.some((id) => `${id}` === `${itemId}`);
            const variantText = item?.variant?.name || item?.size || "";
            const quantity = item?.quantity || 1;
            const price = getPrice(item);

            return (
              <div
                key={`${itemId}-${index}`}
                className="bg-color3 text-color5 rounded-sm px-2 py-2 flex items-center gap-3"
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => toggleItem(itemId)}
                  className="h-4 w-4 accent-yellow-500"
                />
                <div className="min-w-0 grow flex items-center justify-between gap-3 text-sm">
                  <div className="min-w-0">
                    <p className="truncate">{item?.name}</p>
                    <p className="text-color6 text-xs truncate">
                      {variantText ? `${variantText} x${quantity}` : `x${quantity}`}
                    </p>
                  </div>
                  <p className="text-color6 whitespace-nowrap">
                    {(item?.is_complementary || item?.is_loyalty_free || item?.is_offer_free || item?.is_free || price === "Free") ? "Free" : `GBP ${price}`}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        <button
          onClick={handlePrint}
          disabled={isPrinting || loadingPrinters}
          className={`w-full mt-5 py-2.5 rounded-md font-semibold text-black bg-cyellow ${isPrinting ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          {isPrinting ? "Printing..." : "Print"}
        </button>
      </div>
    </div>
  );
}

export default PrintKitchenCopyModal;
